import React, { useCallback, useEffect, useState } from 'react';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';

import RestaurantDashboardLayout from './RestaurantDashboardLayout';
import { useRestaurantEnabledServices } from './useRestaurantEnabledServices';
import OrderStatusPill from './ui/OrderStatusPill';
import {
    PARTY_HALL_QUOTES_LIST_PATH,
    fetchPartyHallQuoteById,
} from './partyHallQuoteApi';
import { formatMobileDisplayOrFallback } from './formatDisplay';
import { PARTY_HALL_TYPE_LABELS } from './restaurantServiceConstants';
import './css/Restaurants.css';
import './css/OrderDetails.css';
import './css/Menu.css';
import './css/Inventory.css';

function formatMoney(value) {
    if (value == null || value === '') return '—';
    const n = Number.parseFloat(value);
    if (!Number.isFinite(n)) return String(value);
    return `₹${n.toFixed(2)}`;
}

function formatDateDisplay(raw) {
    if (!raw) return '—';
    const s = String(raw).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
        const [y, m, d] = s.slice(0, 10).split('-');
        return `${d}/${m}/${y}`;
    }
    return s;
}

function formatHallType(hallType) {
    const code = String(hallType || '').trim().toUpperCase();
    return PARTY_HALL_TYPE_LABELS[code] || hallType || '—';
}

const displayValue = (value) => {
    const text = value != null ? String(value).trim() : '';
    return text || '—';
};

const DetailField = ({ label, value, wide = false, children }) => (
    <div className={`restaurant-detail-field${wide ? ' restaurant-detail-field-wide' : ''}`}>
        <span className="restaurant-detail-label">{label}</span>
        {children ?? <span className="restaurant-detail-value">{displayValue(value)}</span>}
    </div>
);

const PartyHallQuoteDetail = () => {
    const { quoteId: quoteIdParam } = useParams();
    const navigate = useNavigate();
    const { enabledTypes: enabledServiceTypes, loading: servicesLoading } =
        useRestaurantEnabledServices();
    const quoteId = Number(quoteIdParam);

    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const loadQuote = useCallback(async () => {
        if (!Number.isFinite(quoteId) || quoteId <= 0) {
            setError('Invalid quote id.');
            setQuote(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        try {
            const row = await fetchPartyHallQuoteById(quoteId);
            if (!row) {
                setError('Quote not found');
                setQuote(null);
                return;
            }
            setQuote(row);
        } catch (err) {
            setError(
                err?.response?.data?.error ||
                    err?.response?.data?.message ||
                    err.message ||
                    'Failed to load quote'
            );
            setQuote(null);
        } finally {
            setLoading(false);
        }
    }, [quoteId]);

    useEffect(() => {
        loadQuote();
    }, [loadQuote]);

    const handleSelectOrderChannel = (key) => {
        navigate(`/home?tab=orders&orderType=${key}`);
    };

    const backPath =
        quote?.quoteStatus === 'CONFIRMED'
            ? '/home?tab=orders&orderType=partyHall&partyHallView=orders'
            : PARTY_HALL_QUOTES_LIST_PATH;

    return (
        <RestaurantDashboardLayout
            activeTab="orders"
            orderChannel="partyHall"
            onSelectOrderChannel={handleSelectOrderChannel}
            enabledServiceTypes={enabledServiceTypes}
            servicesLoading={servicesLoading}
        >
            <div className="purchase-detail-page store-order-detail-page catering-quote-detail-page">
                <div className="restaurant-detail-panel">
                    {loading ? (
                        <div className="restaurant-detail-card">
                            <div className="restaurant-detail-card-header inventory-item-detail-card-header">
                                <div className="inventory-item-detail-header-row">
                                    <button
                                        type="button"
                                        className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                                        onClick={() => navigate(PARTY_HALL_QUOTES_LIST_PATH)}
                                        aria-label="Back to party hall quotes"
                                    >
                                        <FaArrowLeft aria-hidden />
                                    </button>
                                    <h3 className="restaurant-detail-section-title">
                                        Party Hall Quote #{Number.isFinite(quoteId) ? quoteId : '—'}
                                    </h3>
                                </div>
                            </div>
                            <p className="order-details-loading">Loading quote…</p>
                        </div>
                    ) : null}

                    {!loading && error && !quote ? (
                        <div className="restaurant-detail-card">
                            <div className="restaurant-detail-card-header inventory-item-detail-card-header">
                                <div className="inventory-item-detail-header-row">
                                    <button
                                        type="button"
                                        className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                                        onClick={() => navigate(PARTY_HALL_QUOTES_LIST_PATH)}
                                        aria-label="Back to party hall quotes"
                                    >
                                        <FaArrowLeft aria-hidden />
                                    </button>
                                    <h3 className="restaurant-detail-section-title">
                                        Party Hall Quote #{Number.isFinite(quoteId) ? quoteId : '—'}
                                    </h3>
                                </div>
                            </div>
                            <p className="restaurant-detail-message error">{error}</p>
                        </div>
                    ) : null}

                    {!loading && quote ? (
                        <div className="restaurant-detail-card">
                            <div className="restaurant-detail-card-header inventory-item-detail-card-header">
                                <div className="inventory-item-detail-header-row">
                                    <button
                                        type="button"
                                        className="order-details-back-button restaurant-detail-back-button order-details-back-btn"
                                        onClick={() => navigate(backPath)}
                                        aria-label="Back to party hall quotes"
                                    >
                                        <FaArrowLeft aria-hidden />
                                    </button>
                                    <h3 className="restaurant-detail-section-title">
                                        Party Hall Quote #{quote.partyHallQuoteId}
                                    </h3>
                                    <OrderStatusPill status={quote.quoteStatus ?? 'REQUESTED'} />
                                </div>
                            </div>
                            <div className="restaurant-detail-grid">
                                <DetailField label="Customer" value={quote.customerName || '—'} />
                                <DetailField
                                    label="Phone"
                                    value={formatMobileDisplayOrFallback(quote.customerPhone, '—')}
                                />
                                <DetailField label="Customer ID" value={quote.customerId} />
                                <DetailField label="Hall Name" value={quote.hallName || '—'} />
                                <DetailField label="Hall Type" value={formatHallType(quote.hallType)} />
                                <DetailField label="Event Date" value={formatDateDisplay(quote.eventDate)} />
                                <DetailField label="Event Time" value={quote.eventTime || '—'} />
                                <DetailField label="Event Type" value={quote.eventType || '—'} />
                                <DetailField label="Guests" value={quote.numberOfGuests ?? '—'} />
                                <DetailField label="Estimated Total" value={formatMoney(quote.estimatedTotal)} />
                                <DetailField label="Quoted Total" value={formatMoney(quote.quotedTotal)} />
                                <DetailField label="Created On" value={quote.createdDate || '—'} />
                                <DetailField label="Updated On" value={quote.updatedDate || '—'} />
                                <DetailField label="Address" value={quote.eventAddress || '—'} wide />
                                <DetailField label="Amenities" value={quote.amenityNotes || '—'} wide />
                                <DetailField
                                    label="Special Requests"
                                    value={quote.specialRequests || '—'}
                                    wide
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
        </RestaurantDashboardLayout>
    );
};

export default PartyHallQuoteDetail;
